import { PageType, type ContentContainer, type Config, ProductType, WidgetType } from "../core/types";
import { Logger } from "../core/Logger";
import type { ITrackingService } from "./ITrackingService";
import { version } from '../../package.json'

type VehicleConfig = {
    vin?: string;
    price?: number;
    year?: number;
    make?: string;
    model?: string;
}
type BaseMixpanelProps = {
    widget_type: 'banner' | 'cta' | 'embed';
    product_type: 'calculator' | 'screening' | 'full';
    page_type: 'vdp' | 'srp' | 'finance';
    widget_instance_id: string;
    widget_color: string;
    page_url: string;
    dealer_name?: string;
    vehicle_vin?: string;
    vehicle_price?: number;
    vehicle_year?: number;
    vehicle_make?: string;
    vehicle_model?: string;
    vehicle_location?: string;
    cta_text?: string;   // was: cta_text: string;
};
type MixpanelEvent =
    | { name: 'Widget Opened'; properties: BaseMixpanelProps }
    | { name: 'Widget Closed'; properties: BaseMixpanelProps }
    | { name: 'Calculator Completed'; properties: BaseMixpanelProps }
    | { name: 'Form Field Interaction'; properties: BaseMixpanelProps & {field_name: string; field_value?: string } }
    | { name: 'Form Submitted'; properties: BaseMixpanelProps };

type TrackOptions = { transport?: 'xhr' | 'sendBeacon' };

export class MixpanelService implements ITrackingService {

    constructor(
        private config: Config,
        private isTestMode: boolean = false,
        private isDebug: boolean = false,
    ) {}

    private static readonly INSTANCE_NAME = "selectfi";
    private get mixpanel(): any {
        return (window as any).mixpanel?.[MixpanelService.INSTANCE_NAME];
    }

    initialize(): void {
        if (this.isTestMode) {
            Logger.debug('[MixpanelService - Test Mode] Skipping initialization');
            return;
        }
        const TOKEN = '2902c2498660c0bd02c1e7dba4405422';
        const MIXPANEL_PROXY_HOST = 'https://sdk.selectfi.com/mixpanel';
        (window as any).mixpanel.init(TOKEN, {
            api_host: MIXPANEL_PROXY_HOST,
            lib_base_path: MIXPANEL_PROXY_HOST + '/libs',
            persistence: 'localStorage',
            debug: this.isDebug,
            track_pageview: false
        }, MixpanelService.INSTANCE_NAME);
        this.mixpanel.register({
            sdk_version: version,
        });
    }

    private getWidgetType(container: ContentContainer): 'banner' | 'cta' | 'embed' {
        const typeMap: Record<WidgetType, 'banner' | 'cta' | 'embed'> = {
            [WidgetType.BANNER]: 'banner',
            [WidgetType.CTA]: 'cta',
            [WidgetType.EMBED]: 'embed'
        };
        return typeMap[container.widgetType];
    }

    private getProductType(container: ContentContainer): 'screening' | 'full' | 'calculator' {
        const productNameMap: Record<ProductType, 'screening' | 'full' | 'calculator'> = {
            [ProductType.SCREENING]: 'screening',
            [ProductType.FULL]: 'full',
            [ProductType.CALCULATOR]: 'calculator'
        };
        return productNameMap[container.productType];
    }

    private getPageType(container: ContentContainer): 'vdp' | 'srp' | 'finance' {
        const pageTypeMap: Record<PageType, 'vdp' | 'srp' | 'finance'> = {
            [PageType.VDP]: 'vdp',
            [PageType.SRP]: 'srp',
            [PageType.FINANCE]: 'finance'
        };
        return pageTypeMap[container.pageType] || 'unknown';
    }

    private getWidgetInstanceId(container: ContentContainer): string {
        return container.id;
    }

    private getWidgetColor(container: ContentContainer): string {
        return container.productType === ProductType.CALCULATOR
            ? this.config.styles.calculator.backgroundColor
            : this.config.styles.form.backgroundColor;
    }

    private getVehicleConfig(container: ContentContainer): VehicleConfig {
        // TODO: fetch from asc_dataLayer if available (asc_dataLayer[meta.indexPosition - 1])
        const config: VehicleConfig = {};

        if (container.vin) {
            config.vin = container.vin;
        }

        if (container.vehiclePrice) {
            config.price = container.vehiclePrice;
        }

        if (container.vehicleYear) {
            config.year = container.vehicleYear;
        }

        if (container.vehicleMake) {
            config.make = container.vehicleMake;
        }

        if (container.vehicleModel) {
            config.model = container.vehicleModel;
        }

        return config;
    }

    private getElementText(container: ContentContainer): string {
        if (container.widgetType === WidgetType.BANNER) {
            if (container.productType === ProductType.CALCULATOR) {
                return this.config.images.calculatorBannerUrl;
            } else {
                return this.config.images.formBannerUrl;
            }
        } else if (container.productType === ProductType.CALCULATOR) {
            return this.config.copy.calculator.primaryText + " | " + this.config.copy.calculator.secondaryText;
        } else {
            return this.config.copy.form.primaryText + " | " + this.config.copy.form.secondaryText;
        }
    }

    private pushEvent(event: MixpanelEvent, options?: TrackOptions): void {
        if (this.isTestMode) {
            Logger.debug('[MixpanelService - Test Mode] Event not fired:', event);
            return;
        }

        try {
            const properties = 'properties' in event ? event.properties : undefined;
            this.mixpanel.track(event.name, properties, options ?? {});
            Logger.debug('[MixpanelService] Event pushed:', event.name);
        } catch (error) {
            Logger.error('[MixpanelService] Failed to push event:', error);
        }
    }

    trackOpen(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);

        this.pushEvent({
            name: 'Widget Opened', properties: {
            widget_type: this.getWidgetType(container),
            product_type: this.getProductType(container),
            page_type: this.getPageType(container),
            widget_instance_id: this.getWidgetInstanceId(container),
            widget_color: this.getWidgetColor(container),
            page_url: window.location.href,
            ...(container.dealerName!==undefined && container.dealerName!==null && {dealer_name: container.dealerName}),
            ...(vehicleConfig.vin!==undefined && vehicleConfig.vin!==null && {vehicle_vin: vehicleConfig.vin}),
            ...(vehicleConfig.price!==undefined && vehicleConfig.price!==null && {vehicle_price: vehicleConfig.price}),
            ...(vehicleConfig.year!==undefined && vehicleConfig.year!==null && {vehicle_year: vehicleConfig.year}),
            ...(vehicleConfig.make!==undefined && vehicleConfig.make!==null && {vehicle_make: vehicleConfig.make}),
            ...(vehicleConfig.model!==undefined && vehicleConfig.model!==null && {vehicle_model: vehicleConfig.model}),
            ...(container.dealerName!==undefined && container.dealerName!==null && {vehicle_location: container.dealerName}),
            cta_text: this.getElementText(container)
            }
        });
    }

    trackClose(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);

        this.pushEvent({
            name: 'Widget Closed', properties: {
            widget_type: this.getWidgetType(container),
            product_type: this.getProductType(container),
            page_type: this.getPageType(container),
            widget_instance_id: this.getWidgetInstanceId(container),
            widget_color: this.getWidgetColor(container),
            page_url: window.location.href,
            ...(container.dealerName!==undefined && container.dealerName!==null && {dealer_name: container.dealerName}),
            ...(vehicleConfig.vin!==undefined && vehicleConfig.vin!==null && {vehicle_vin: vehicleConfig.vin}),
            ...(vehicleConfig.price!==undefined && vehicleConfig.price!==null && {vehicle_price: vehicleConfig.price}),
            ...(vehicleConfig.year!==undefined && vehicleConfig.year!==null && {vehicle_year: vehicleConfig.year}),
            ...(vehicleConfig.make!==undefined && vehicleConfig.make!==null && {vehicle_make: vehicleConfig.make}),
            ...(vehicleConfig.model!==undefined && vehicleConfig.model!==null && {vehicle_model: vehicleConfig.model}),
            ...(container.dealerName!==undefined && container.dealerName!==null && {vehicle_location: container.dealerName}),
            cta_text: this.getElementText(container)
            }},
            {transport: 'sendBeacon'}
        );
    }

    trackFieldInteraction(container: ContentContainer, fieldName: string, fieldValue?: string): void {
        const vehicleConfig = this.getVehicleConfig(container);
        
        this.pushEvent({
            name: 'Form Field Interaction', properties: {
            widget_type: this.getWidgetType(container),
            product_type: this.getProductType(container),
            page_type: this.getPageType(container),
            widget_instance_id: this.getWidgetInstanceId(container),
            widget_color: this.getWidgetColor(container),
            page_url: window.location.href,
            ...(container.dealerName!==undefined && container.dealerName!==null && {dealer_name: container.dealerName}),
            ...(vehicleConfig.vin!==undefined && vehicleConfig.vin!==null && {vehicle_vin: vehicleConfig.vin}),
            ...(vehicleConfig.price!==undefined && vehicleConfig.price!==null && {vehicle_price: vehicleConfig.price}),
            ...(vehicleConfig.year!==undefined && vehicleConfig.year!==null && {vehicle_year: vehicleConfig.year}),
            ...(vehicleConfig.make!==undefined && vehicleConfig.make!==null && {vehicle_make: vehicleConfig.make}),
            ...(vehicleConfig.model!==undefined && vehicleConfig.model!==null && {vehicle_model: vehicleConfig.model}),
            ...(container.dealerName!==undefined && container.dealerName!==null && {vehicle_location: container.dealerName}),
            field_name: fieldName,
            ...(fieldName==='credit_score' && fieldValue!==undefined && {field_value: fieldValue}),
            cta_text: this.getElementText(container),
            }
        });
    }

    trackSubmit(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);
        
        this.pushEvent({
            name: 'Form Submitted', properties: {
            widget_type: this.getWidgetType(container),
            product_type: this.getProductType(container),
            page_type: this.getPageType(container),
            widget_instance_id: this.getWidgetInstanceId(container),
            widget_color: this.getWidgetColor(container),
            page_url: window.location.href,
            ...(container.dealerName!==undefined && container.dealerName!==null && {dealer_name: container.dealerName}),
            ...(vehicleConfig.vin!==undefined && vehicleConfig.vin!==null && {vehicle_vin: vehicleConfig.vin}),
            ...(vehicleConfig.price!==undefined && vehicleConfig.price!==null && {vehicle_price: vehicleConfig.price}),
            ...(vehicleConfig.year!==undefined && vehicleConfig.year!==null && {vehicle_year: vehicleConfig.year}),
            ...(vehicleConfig.make!==undefined && vehicleConfig.make!==null && {vehicle_make: vehicleConfig.make}),
            ...(vehicleConfig.model!==undefined && vehicleConfig.model!==null && {vehicle_model: vehicleConfig.model}),
            ...(container.dealerName!==undefined && container.dealerName!==null && {vehicle_location: container.dealerName}),
    
            }
        });
    }

    trackContinueToCreditApp(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);
        
        this.pushEvent({
            name: 'Calculator Completed', properties: {
            widget_type: this.getWidgetType(container),
            product_type: this.getProductType(container),
            page_type: this.getPageType(container),
            widget_instance_id: this.getWidgetInstanceId(container),
            widget_color: this.getWidgetColor(container),
            page_url: window.location.href,
            ...(container.dealerName!==undefined && container.dealerName!==null && {dealer_name: container.dealerName}),
            ...(vehicleConfig.vin!==undefined && vehicleConfig.vin!==null && {vehicle_vin: vehicleConfig.vin}),
            ...(vehicleConfig.price!==undefined && vehicleConfig.price!==null && {vehicle_price: vehicleConfig.price}),
            ...(vehicleConfig.year!==undefined && vehicleConfig.year!==null && {vehicle_year: vehicleConfig.year}),
            ...(vehicleConfig.make!==undefined && vehicleConfig.make!==null && {vehicle_make: vehicleConfig.make}),
            ...(vehicleConfig.model!==undefined && vehicleConfig.model!==null && {vehicle_model: vehicleConfig.model}),
            ...(container.dealerName!==undefined && container.dealerName!==null && {vehicle_location: container.dealerName}),
            cta_text: this.getElementText(container)
            }},
            {transport: 'sendBeacon'}
        );
    }
}
